'use client'

import * as React from 'react'

import { ResizeHandle, ResizeHandler, Resizer } from '@saas-ui-pro/react'
import { useModals } from '@saas-ui/modals'
import {
  Badge,
  Box,
  Command,
  IconButton,
  Sidebar,
  Spacer,
  Tooltip,
  useSidebar,
} from '@saas-ui/react'
import { useHotkeysShortcut } from '@saas-ui/use-hotkeys'
import { type LinkProps, createLink, useNavigate } from '@tanstack/react-router'

import { FRICTION_FEATURE_FLAG, hasWorkspaceFeature } from '@trevize/config'

import { FeedbackDialog } from '#components/feedback-dialog.tsx'
import { BarrierBlockIcon } from '#components/icons/barrier-block-icon.tsx'
import { BrainIcon } from '#components/icons/brain-icon.tsx'
import { ChartBarIcon } from '#components/icons/chart-bar-icon.tsx'
import { ClockIcon } from '#components/icons/clock-icon.tsx'
import { Message2CodeIcon } from '#components/icons/message-2-code-icon.tsx'
import { Message2HeartIcon } from '#components/icons/message-2-heart-icon.tsx'
import { PlusIcon } from '#components/icons/plus-icon.tsx'
import { RefreshDotIcon } from '#components/icons/refresh-dot-icon.tsx'
import { SmartHomeIcon } from '#components/icons/smart-home-icon.tsx'
import { usePreloadHomeTasks } from '#features/home/use-preload-home-tasks.ts'
import { SidebarImplementations } from '#features/implementations/components/sidebar-implementations'
import { usePreloadActiveTaskDetails } from '#features/implementations/use-preload-active-task-details.ts'
import { useUserSettings } from '#lib/user-settings/use-user-settings'

import { useCurrentWorkspace } from '../hooks/use-current-workspace'
import { useWorkspace } from '../hooks/use-workspace'
import { CreateDialog } from './create-dialog'
import { useCreateDialogActions } from './create-dialog-store'
import { InvitePeopleDialog } from './invite-people'
import { WorkspacesMenu } from './workspaces-menu'

export interface AppSidebarProps extends Sidebar.RootProps {}

export const AppSidebar: React.FC<AppSidebarProps> = (props) => {
  const modals = useModals()

  const workspace = useWorkspace()
  const [workspaceData] = useCurrentWorkspace()
  usePreloadHomeTasks(workspaceData.id)
  usePreloadActiveTaskDetails(workspaceData.id)

  const [{ sidebarWidth }, setUserSettings] = useUserSettings()

  const onResize: ResizeHandler = ({ width }) => {
    setUserSettings('sidebarWidth', width)
  }

  const { mode, setMode, open, setOpen, isMobile } = useSidebar()

  return (
    <Resizer
      defaultWidth={sidebarWidth}
      onResize={onResize}
      enabled={!isMobile && open}
    >
      <Sidebar.Root {...props}>
        <Sidebar.Header alignItems="center" gap="2">
          <React.Suspense fallback={null}>
            <WorkspacesMenu />
          </React.Suspense>

          <Spacer />
          <CreateButton />
        </Sidebar.Header>

        <Sidebar.Body>
          <Sidebar.Group>
            <AppSidebarLink
              to="/$workspace"
              params={{ workspace }}
              activeOptions={{ exact: true }}
              label="Home"
              icon={<SmartHomeIcon />}
              hotkey="navigation.home"
            />
            <AppSidebarLink
              to="/$workspace/sessions"
              params={{ workspace }}
              activeOptions={{ exact: false }}
              label="Sessions"
              icon={<Message2CodeIcon />}
            />
            <AppSidebarLink
              to="/$workspace/insights"
              params={{ workspace }}
              activeOptions={{ exact: false }}
              label="Insights"
              icon={<ChartBarIcon />}
              hotkey="navigation.insights"
            />
            {hasWorkspaceFeature(workspaceData, FRICTION_FEATURE_FLAG) && (
              <AppSidebarLink
                to="/$workspace/friction"
                params={{ workspace }}
                activeOptions={{ exact: false }}
                label="Friction"
                icon={<BarrierBlockIcon />}
              />
            )}
            {workspaceData.knowledgeEnabled && (
              <AppSidebarLink
                to="/$workspace/knowledge"
                params={{ workspace }}
                activeOptions={{ exact: false }}
                label="Knowledge"
                icon={<BrainIcon />}
                hotkey="navigation.knowledge"
              />
            )}
          </Sidebar.Group>

          <Sidebar.Group>
            <Sidebar.GroupHeader>
              <Sidebar.GroupTitle>Agents</Sidebar.GroupTitle>
            </Sidebar.GroupHeader>
            <Sidebar.GroupContent>
              <AppSidebarLink
                to="/$workspace/automations"
                params={{ workspace }}
                activeOptions={{ exact: false }}
                label="Automations"
                icon={<ClockIcon />}
                hotkey="navigation.automations"
              />

              <AppSidebarLink
                to="/$workspace/tasks"
                params={{ workspace }}
                activeOptions={{ exact: true }}
                label="Tasks"
                icon={<RefreshDotIcon />}
                hotkey="navigation.implementations"
              />
            </Sidebar.GroupContent>
          </Sidebar.Group>

          <SidebarImplementations />

          <Spacer />

          <Sidebar.Group>
            <Sidebar.NavItem onClick={() => modals.open(FeedbackDialog)}>
              <Sidebar.NavButton>
                <Message2HeartIcon />
                Feedback
              </Sidebar.NavButton>
            </Sidebar.NavItem>
            <Sidebar.NavItem onClick={() => modals.open(InvitePeopleDialog)}>
              <Sidebar.NavButton>
                <PlusIcon />
                Invite people
              </Sidebar.NavButton>
            </Sidebar.NavItem>
          </Sidebar.Group>
        </Sidebar.Body>

        {/* <Sidebar.Footer>
          <BillingStatus />

          <HStack>
            <Menu.Root>
              <Menu.Trigger asChild>
                <IconButton
                  variant="surface"
                  size="xs"
                  rounded="full"
                  aria-label="Search"
                >
                  ?
                </IconButton>
              </Menu.Trigger>
              <Menu.Content minW="200px">
                <Menu.ItemGroup title="Help">
                  <Menu.Item asChild value="discord">
                    <a href="https://saas-ui.dev/discord" target="_blank">
                      Discord community
                    </a>
                  </Menu.Item>
                </Menu.ItemGroup>
              </Menu.Content>
            </Menu.Root>
          </HStack>
        </Sidebar.Footer> */}

        <Sidebar.Track
          asChild
          onClick={() => {
            if (mode === 'flyout') {
              setMode('collapsible')
              setOpen(true)
            } else {
              setMode('flyout')
            }
          }}
        >
          <ResizeHandle />
        </Sidebar.Track>
      </Sidebar.Root>
    </Resizer>
  )
}

function CreateButton() {
  const actions = useCreateDialogActions()

  const command = useHotkeysShortcut('general.create', () => actions.open(), [])

  return (
    <>
      <Tooltip
        content={
          <>
            Create new <Command size="xs">{command}</Command>
          </>
        }
        positioning={{ placement: 'bottom' }}
        openDelay={600}
        closeDelay={50}
        portalled
      >
        <IconButton
          variant="surface"
          size="xs"
          aria-label="Create new"
          onClick={() => actions.open()}
        >
          <PlusIcon />
        </IconButton>
      </Tooltip>
      <CreateDialog />
    </>
  )
}

// function SidebarProjects() {
//   const [projects, setProjects] = React.useState([
//     { label: 'Research', value: 'research' },
//     { label: 'SaaS', value: 'saas' },
//     { label: 'Marketing', value: 'marketing' },
//   ])
//   return (
//     <Collapsible.Root defaultOpen asChild>
//       <Sidebar.Group>
//         <Collapsible.Trigger asChild>
//           <Sidebar.GroupHeader>
//             <Sidebar.GroupTitle>Projects</Sidebar.GroupTitle>
//             <Sidebar.GroupEndElement opacity={0} _groupHover={{ opacity: 1 }}>
//               <IconButton variant="ghost" size="xs" aria-label="Add project">
//                 <PlusIcon />
//               </IconButton>
//             </Sidebar.GroupEndElement>
//           </Sidebar.GroupHeader>
//         </Collapsible.Trigger>
//         <Collapsible.Content>
//           <Sidebar.GroupContent>
//             <Reorder.Group values={projects} onReorder={setProjects}>
//               {projects.map((project) => (
//                 <Reorder.Item key={project.value} value={project}>
//                   <Sidebar.NavItem size="sm">
//                     <Sidebar.NavButton px="2">
//                       <FolderIcon />
//                       {project.label}
//                     </Sidebar.NavButton>
//                   </Sidebar.NavItem>
//                 </Reorder.Item>
//               ))}
//             </Reorder.Group>
//           </Sidebar.GroupContent>
//         </Collapsible.Content>
//       </Sidebar.Group>
//     </Collapsible.Root>
//   )
// }

interface AppSidebarlink
  extends
    Sidebar.NavItemProps,
    Pick<LinkProps, 'to' | 'params' | 'activeOptions'> {
  hotkey?: string
  label: string
  icon: React.ReactNode
  badge?: React.ReactNode
}

const AppSidebarLink = (props: AppSidebarlink) => {
  const { to, params, activeOptions, icon, label, hotkey, badge, ...rest } =
    props

  const navigate = useNavigate({ from: '/$workspace/' })

  const command = useHotkeysShortcut(
    hotkey ?? '',
    () => {
      navigate({ to, params })
    },
    [to],
  )

  return (
    <Tooltip
      content={
        <>
          Go to {label.toLowerCase()} <Command size="xs">{command}</Command>
        </>
      }
      positioning={{ placement: 'right' }}
      openDelay={600}
      closeDelay={50}
      portalled
    >
      <Sidebar.NavItem {...rest}>
        <NavLink
          to={to}
          params={params}
          activeProps={{ 'data-active': true }}
          activeOptions={activeOptions}
        >
          {icon}

          <Box as="span" lineClamp={1}>
            {label}
          </Box>

          {typeof badge !== 'undefined' ? (
            <Badge borderRadius="sm" ms="auto" px="1.5" bg="none">
              {badge}
            </Badge>
          ) : null}
        </NavLink>
      </Sidebar.NavItem>
    </Tooltip>
  )
}

const NavLink = createLink(Sidebar.NavButton)
